import React, { useEffect, useRef } from "react";
import clsx from "clsx";

import { Box, Button, TextField, useMediaQuery, useTheme } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { alpha } from "@material-ui/core/styles/colorManipulator";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minWidth: 0,
  },
  controlsRow: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    minWidth: 0,
    gap: theme.spacing(1),
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(1.25),
    },
  },
  input: {
    flex: 1,
    minWidth: 0,
    // In column layout (phones / small tablets), avoid flex-grow on the main axis so the
    // field does not stretch vertically inside nested flex parents (e.g. track rows).
    [theme.breakpoints.down("md")]: {
      flex: "0 1 auto",
      alignSelf: "stretch",
      width: "100%",
      maxWidth: "100%",
    },
    WebkitTapHighlightColor: "transparent",
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.spacing(0.875),
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
      [theme.breakpoints.down("md")]: {
        // Coarse touch + readability on phones / small tablets
        minHeight: 48,
      },
      "& fieldset": {
        borderColor: theme.palette.divider,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.text.secondary,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.divider,
        borderWidth: 1,
      },
    },
    "& .MuiInputLabel-outlined": {
      color: theme.palette.text.secondary,
      [theme.breakpoints.down("md")]: {
        fontSize: "0.95rem",
      },
    },
    "& .MuiInputLabel-outlined.Mui-focused": {
      color: theme.palette.text.secondary,
    },
    "& .MuiInputLabel-outlined.Mui-error": {
      color: theme.palette.error.main,
    },
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
      textTransform: "uppercase",
      fontSize: 13,
      fontWeight: 600,
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      [theme.breakpoints.down("md")]: {
        // iOS Safari avoids auto-zoom when focused if input text is ≥16px
        fontSize: 16,
        paddingTop: theme.spacing(1.25),
        paddingBottom: theme.spacing(1.25),
      },
    },
    "& .MuiFormHelperText-root": {
      marginTop: theme.spacing(0.5),
      marginLeft: theme.spacing(0.25),
      [theme.breakpoints.down("md")]: {
        fontSize: "0.875rem",
        lineHeight: 1.35,
      },
    },
  },
  /** Uses palette error (not raw reds) so light/dark theme and contrast settings stay coherent. */
  inputMismatch: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: alpha(theme.palette.error.main, 0.14),
      "@media (prefers-contrast: more)": {
        backgroundColor: alpha(theme.palette.error.main, 0.22),
      },
      "& fieldset": {
        borderColor: theme.palette.error.main,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.error.dark,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.error.main,
        borderWidth: 2,
      },
    },
  },
  validateButton: {
    flexShrink: 0,
    minWidth: theme.spacing(13),
    height: 40,
    borderRadius: theme.spacing(0.875),
    whiteSpace: "nowrap",
    textTransform: "none",
    fontWeight: 700,
    boxShadow: "none",
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    WebkitTapHighlightColor: "transparent",
    [theme.breakpoints.down("md")]: {
      minHeight: 48,
      height: "auto",
      paddingTop: theme.spacing(1.25),
      paddingBottom: theme.spacing(1.25),
      fontSize: "1rem",
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      alignSelf: "stretch",
      // MUI default is nowrap; allow wrapping for longer localized labels on narrow widths.
      whiteSpace: "normal",
      lineHeight: 1.25,
    },
  },
}));

export const InlineRailcarVerificationControls = ({
  label,
  inputValue,
  inputError = false,
  helperText = "",
  onInputChange,
  onValidate,
  validateButtonText = "Validate",
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isNarrowViewport = useMediaQuery(theme.breakpoints.down("md"));
  const inputRef = useRef(null);

  useEffect(() => {
    const focusTimer = setTimeout(() => {
      inputRef.current?.focus();
    }, 0);

    return () => clearTimeout(focusTimer);
  }, []);

  return (
    <Box className={classes.root}>
      <Box className={classes.controlsRow}>
        <TextField
          inputRef={inputRef}
          className={clsx(classes.input, inputError && classes.inputMismatch)}
          label={label}
          InputLabelProps={{ shrink: true }}
          value={inputValue || ""}
          onChange={(evt) => onInputChange(evt.target.value)}
          onKeyDown={(evt) => {
            if (evt.key === "Enter" && inputValue?.trim()) {
              onValidate();
            }
          }}
          error={inputError}
          helperText={helperText}
          variant="outlined"
          size={isNarrowViewport ? "medium" : "small"}
          fullWidth
          inputProps={{
            style: { textTransform: "uppercase" },
            enterKeyHint: "go",
            autoCapitalize: "characters",
            autoCorrect: "off",
            spellCheck: false,
          }}
        />
        <Button
          className={classes.validateButton}
          variant="contained"
          color="primary"
          onClick={onValidate}
          disabled={!inputValue?.trim()}
        >
          {validateButtonText}
        </Button>
      </Box>
    </Box>
  );
};
